// The departure board on /ielts/calculator.
//
// ⛔ No React and no arithmetic. The server has already rendered a complete
// board for the fader's starting position, and every other position is in the
// page as data — so this file finds elements, looks a row up, and redraws the
// headline, the timeline and the cap mark. With scripting off the board is
// still a whole answer.
//
// ⛔ Nothing here runs on a timer. The board changes only when new information
// arrives — the reader moved the fader or flipped the schedule — which is what
// a real board does, and it is the reason this world can be built at all: a
// countdown is a copy red line on this product.

import { setupCurveBuilder } from 'utils/ieltsCurve'
import { selectVariant, formatNumber } from 'utils/ieltsPlan'
import { weekExtent, weekScale, bandScale, weekTicks, splitAtExam } from 'utils/ieltsTimeline'

const SVG = 'http://www.w3.org/2000/svg'

function svgNode (name, attributes = {}, text) {
  const node = document.createElementNS(SVG, name)
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value))
  if (text !== undefined) node.textContent = text
  return node
}

const band = (value) => Number(value).toFixed(1)
const fill = (template, week) => String(template).replaceAll('__week__', week)

// A JSON data attribute, or the fallback when it is missing or unreadable.
function readData (node, key, fallback) {
  try {
    return node && node.dataset[key] ? JSON.parse(node.dataset[key]) : fallback
  } catch (error) {
    return fallback
  }
}

// The headline for a row, rebuilt from the parts the server wrote. ⛔ textContent
// only: the sentence is the server's, the browser just lays it out.
function renderHeadline (node, parts) {
  if (!node || !Array.isArray(parts) || parts.length === 0) return

  node.replaceChildren(...parts.map(([text, figure]) => {
    if (!figure) return document.createTextNode(text)

    const span = document.createElement('span')
    span.className = 'brd-headfig'
    span.textContent = text
    return span
  }))
}

// The reader's path against the exam week, for one precomputed row. A row the
// server sent no milestones for leaves the timeline hidden: the headline above
// it already says everything the drawing would. `chart` is read once at setup.
function drawTimeline (container, chart, row) {
  if (!container) return

  const milestones = row && Array.isArray(row.milestones) ? row.milestones : []
  const { copy, startBand, targetBand, examWeek } = chart

  if (milestones.length === 0 || !(targetBand > startBand) || !(examWeek > 0)) {
    container.hidden = true
    return
  }

  container.hidden = false
  // ⛔ The content box, not clientWidth: the container's padding would make the
  // viewBox wider than the drawing and scale every label down on a phone.
  const style = window.getComputedStyle(container)
  const inner = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
  const width = Math.max(260, Math.round(inner || 900))

  // Most drag pixels land on the row that is already drawn.
  const key = `${width}|${row.dailyHours}|${row.schedule}`
  if (container.dataset.ieltsDrawn === key) return
  container.dataset.ieltsDrawn = key

  const frame = { left: 44, right: width - 14, top: 26, bottom: 188 }
  const last = milestones[milestones.length - 1].week
  const extent = weekExtent(examWeek, last)
  const x = weekScale(extent, frame)
  const y = bandScale(startBand, targetBand, frame)
  const { before, after, crossing } = splitAtExam(startBand, milestones, examWeek, row.bandAtExam && row.bandAtExam.position)
  const late = crossing !== null

  const svg = svgNode('svg', { viewBox: `0 0 ${width} 230`, role: 'img', 'aria-label': copy.label || '' })

  // Each milestone is one half band, so the scale is the start plus them.
  ;[startBand, ...milestones.map((milestone) => milestone.band)].forEach((value) => {
    const isTarget = value === targetBand
    svg.append(
      svgNode('line', { x1: frame.left, x2: frame.right, y1: y(value), y2: y(value), class: isTarget ? 'brd-tl-target' : 'brd-tl-grid' }),
      svgNode('text', { x: frame.left - 8, y: y(value) + 5, 'text-anchor': 'end', class: isTarget ? 'brd-tl-band brd-tl-band--target' : 'brd-tl-band' }, band(value))
    )
  })

  weekTicks(extent, width).forEach((week) => {
    svg.append(svgNode('text', { x: x(week), y: frame.bottom + 24, 'text-anchor': 'middle', class: 'brd-tl-tick' },
      week === 0 ? (copy.now || '') : fill(copy.tick || '__week__', week)))
  })

  svg.append(svgNode('rect', {
    x: x(examWeek), y: frame.top - 14, width: Math.max(0, frame.right - x(examWeek)), height: frame.bottom - frame.top + 14,
    class: late ? 'brd-tl-after brd-tl-after--late' : 'brd-tl-after'
  }))

  const line = (points) => points.map(([week, value], index) => `${index ? 'L' : 'M'}${x(week)},${y(value)}`).join(' ')
  svg.append(svgNode('path', { d: line(before), class: 'brd-tl-path' }))
  if (after.length) svg.append(svgNode('path', { d: line(after), class: 'brd-tl-path brd-tl-path--after' }))

  const examX = x(examWeek)
  const flip = examX > width - 150
  svg.append(
    svgNode('line', { x1: examX, x2: examX, y1: frame.top - 14, y2: frame.bottom, class: 'brd-tl-exam' }),
    svgNode('text', { x: examX + (flip ? -6 : 6), y: frame.top - 2, 'text-anchor': flip ? 'end' : 'start', class: 'brd-tl-examlabel' },
      fill(copy.exam || '__week__', examWeek))
  )

  milestones.forEach((milestone) => {
    const missed = milestone.week > examWeek
    svg.append(svgNode('circle', { cx: x(milestone.week), cy: y(milestone.band), r: 6, class: missed ? 'brd-tl-node brd-tl-node--missed' : 'brd-tl-node' }))
    // ⛔ A label right on the exam line collides with the exam's own label.
    if (Math.abs(x(milestone.week) - examX) > 60) {
      svg.append(svgNode('text', { x: x(milestone.week), y: y(milestone.band) - 12, 'text-anchor': 'middle', class: missed ? 'brd-tl-mark brd-tl-mark--missed' : 'brd-tl-mark' },
        fill(copy.milestone || '__week__', Math.ceil(milestone.week))))
    }
  })

  if (late) {
    svg.append(
      svgNode('circle', { cx: examX, cy: y(crossing[1]), r: 7, class: 'brd-tl-here' }),
      svgNode('text', { x: examX + (flip ? -12 : 12), y: y(crossing[1]) + 24, 'text-anchor': flip ? 'end' : 'start', class: 'brd-tl-herelabel' }, copy.here || '')
    )
  }

  container.replaceChildren(svg)
}

function setup () {
  const root = document.querySelector('[data-ielts-variants]')
  if (!root) return

  let variants = []
  try {
    variants = JSON.parse(root.dataset.ieltsVariants)
  } catch (error) {
    // A payload we cannot read leaves the server-rendered board exactly as it is.
    return
  }

  const slider = document.querySelector('[data-ielts-daily]')
  if (!slider) return

  const schedules = Array.from(document.querySelectorAll('[data-ielts-schedule]'))
  const capNote = document.querySelector('[data-ielts-cap-note]')
  const headline = document.querySelector('[data-ielts-headline]')
  const timeline = document.querySelector('[data-ielts-timeline]')
  const capMark = document.querySelector('[data-ielts-cap-mark]')
  const overZone = document.querySelector('[data-ielts-over-zone]')
  const faderRange = readData(document.querySelector('[data-ielts-fader-range]'), 'ieltsFaderRange', null)
  const capPositions = readData(capMark, 'ieltsCapPositions', {})
  const capLabels = readData(capMark, 'ieltsCapLabels', {})
  const chart = timeline && {
    copy: readData(timeline, 'ieltsTimelineCopy', {}),
    startBand: Number(timeline.dataset.ieltsStartBand),
    targetBand: Number(timeline.dataset.ieltsTargetBand),
    examWeek: Number(timeline.dataset.ieltsExamWeek)
  }
  const dailyLabel = document.querySelector('[data-ielts-daily-label]')

  const currentSchedule = () => {
    const checked = schedules.find((input) => input.checked)
    return checked ? checked.value : root.dataset.ieltsDefaultSchedule
  }

  // Where the schedule's cap sits on the control (the server's positions),
  // and the stretch above it that counts at half.
  const placeCap = (schedule, hours) => {
    const cap = capPositions[schedule]
    if (!capMark || !faderRange || !Number.isFinite(cap)) return

    capMark.style.left = `${cap}%`
    const label = capMark.querySelector('span')
    if (label && capLabels[schedule]) label.textContent = capLabels[schedule]

    if (overZone) {
      const at = Math.round(((hours - faderRange.min) / (faderRange.max - faderRange.min)) * 1000) / 10
      overZone.style.width = `${Math.max(0, at - cap)}%`
      overZone.style.left = `${cap}%`
    }
  }

  const apply = () => {
    const variant = selectVariant(variants, currentSchedule(), Number(slider.value))
    if (!variant) return

    if (dailyLabel) dailyLabel.textContent = formatNumber(slider.value)

    // The drawing and the sentence follow the thumb: a line that tracks the
    // hand is the point of the drawing.
    placeCap(currentSchedule(), Number(slider.value))
    renderHeadline(headline, variant.headlineParts)
    drawTimeline(timeline, chart, variant)

    if (capNote && capNote.dataset.ieltsCapTemplate) {
      // ⛔ Shown exactly when something was discounted. Always saying "you
      // entered 3, we count 3" teaches the reader to skip the note; never
      // saying it lets the discount happen silently.
      const discounted = Number(slider.value) > Number(variant.effectiveDaily)
      capNote.hidden = !discounted

      if (discounted) {
        capNote.textContent = capNote.dataset.ieltsCapTemplate
          .replaceAll('__entered__', formatNumber(slider.value))
          // ⛔ Not formatNumber: 4.5 part-time counts as 3.75, and the server
          // prints 3.75 (`%g`). Rounding it to 3.8 made the note disagree with
          // the page it was just rendered on. String() prints it the same way.
          .replaceAll('__effective__', String(Number(variant.effectiveDaily)))
      }
    }
  }

  slider.addEventListener('input', apply)
  slider.addEventListener('change', apply)
  schedules.forEach((input) => input.addEventListener('change', apply))
  apply()

  let resizeFrame = null
  window.addEventListener('resize', () => {
    if (resizeFrame) window.cancelAnimationFrame(resizeFrame)
    resizeFrame = window.requestAnimationFrame(apply)
  })
}

// ⛔ The builder mounts on its own, not inside `setup`. `setup` returns early
// when there is no fader, and there is no fader until the reader has entered a
// combination — but the section that explains the figures is on the page from
// the first paint, and that is where the builder lives.
// Says so the moment both choices are made, rather than after a page load.
//
// ⛔ It does not block the submit and it does not open anything. The reader has
// made a coherent choice with a correct answer, and someone who types the same
// address gets that answer either way — so the note is a heads-up, not a gate.
function setupAboveTargetNote () {
  const note = document.querySelector('[data-ielts-above-target]')
  const target = document.querySelector('select[name="target"]')
  const level = document.querySelector('select[name="level"]')
  if (!note || !target || !level) return

  // The level options carry their band in the token, e.g. `band-4.5`.
  // ⚠️ An address shared before the self-assessment group left the form carries
  // a token this control has no option for, so `level.value` is empty and this
  // reads null. The note stays hidden until the server answers on submit, and
  // the server does print it.
  const bandOf = (value) => {
    const match = String(value).match(/(\d+(?:\.\d+)?)/)
    return match ? Number(match[1]) : null
  }

  const update = () => {
    // ⛔ An unchosen option is not a zero. `Number('')` is 0 and passes
    // isFinite, so resetting the target to the blank option made the note claim
    // the reader was already there.
    const chosen = target.value === '' ? null : Number(target.value)
    const at = bandOf(level.value)

    note.hidden = !(chosen !== null && Number.isFinite(chosen) && at !== null && chosen <= at)
  }

  target.addEventListener('change', update)
  level.addEventListener('change', update)
}

function boot () {
  setup()
  setupCurveBuilder()
  setupAboveTargetNote()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}
